import type { ProductEventEnvelope } from "../productAnalytics";
import type {
  WebAnalyticsIdentityContext,
  WebAnalyticsProvider,
} from "../web/webProductAnalyticsRuntime";
import type { Types } from "@amplitude/analytics-browser";

interface AmplitudeSdk {
  init(
    apiKey: string,
    options: {
      serverZone: "EU";
      autocapture: false;
      defaultTracking: false;
      customEnrichment: false;
      enableDiagnostics: false;
      fetchRemoteConfig: false;
      identityStorage: "none";
      optOut: false;
      trackingOptions: {
        ipAddress: false;
        language: false;
        platform: false;
      };
    },
  ): { promise: Promise<unknown> };
  add: typeof import("@amplitude/analytics-browser").add;
  track(event: {
    event_type: string;
    insert_id: string;
    device_id: string;
    session_id: number;
    time: number;
    app_version: string;
    platform: string;
    event_properties: Record<string, string | boolean | number>;
  }): unknown;
  setOptOut(optOut: boolean): void;
  reset(): void;
}

type LoadAmplitudeSdk = () => Promise<AmplitudeSdk>;

const loadAmplitudeSdk: LoadAmplitudeSdk = async () =>
  import("@amplitude/analytics-browser");

const amplitudeOptions = {
  serverZone: "EU",
  autocapture: false,
  defaultTracking: false,
  customEnrichment: false,
  enableDiagnostics: false,
  fetchRemoteConfig: false,
  identityStorage: "none",
  optOut: false,
  trackingOptions: {
    ipAddress: false,
    language: false,
    platform: false,
  },
} as const;

const privacyBoundaryPlugin: Types.BeforePlugin = {
  name: "pantryverse-product-analytics-privacy-boundary",
  type: "before" as const,
  async execute(event: Types.Event): Promise<Types.Event> {
    return {
      event_type: event.event_type,
      insert_id: event.insert_id,
      device_id: event.device_id,
      session_id: event.session_id,
      time: event.time,
      app_version: event.app_version,
      platform: event.platform,
      event_properties: event.event_properties,
    };
  },
};

export class AmplitudeWebSink implements WebAnalyticsProvider {
  private sdk: AmplitudeSdk | null = null;
  private sdkPromise: Promise<AmplitudeSdk> | null = null;
  private optedOut = false;

  constructor(
    private readonly apiKey: string,
    private readonly loadSdk: LoadAmplitudeSdk = loadAmplitudeSdk,
  ) {}

  async activate(): Promise<void> {
    this.optedOut = false;
    const sdk = await this.client();
    if (this.optedOut) {
      this.optOutAndReset(sdk);
      return;
    }
    sdk.setOptOut(false);
  }

  record(
    envelope: ProductEventEnvelope,
    identity: WebAnalyticsIdentityContext,
  ): void {
    if (!this.sdk || this.optedOut) return;
    try {
      this.sdk.track({
        event_type: envelope.eventName,
        insert_id: envelope.eventId,
        device_id: identity.guestBrowserIdentity,
        session_id: identity.guestAnalyticsSessionIdentity,
        time: Date.parse(envelope.occurredAt),
        app_version: envelope.appVersion,
        platform: envelope.platform,
        event_properties: {
          ...envelope.properties,
          event_schema_version: envelope.eventSchemaVersion,
          environment: envelope.environment,
          build_number: envelope.buildNumber,
          build_channel: envelope.buildChannel,
        },
      });
    } catch {
      // Provider delivery is best effort and never affects product behavior.
    }
  }

  deactivate(): void {
    this.optedOut = true;
    if (this.sdk) {
      this.optOutAndReset(this.sdk);
      return;
    }
    if (this.sdkPromise) {
      void this.sdkPromise
        .then((sdk) => {
          if (this.optedOut) this.optOutAndReset(sdk);
        })
        .catch(() => undefined);
    }
  }

  private client(): Promise<AmplitudeSdk> {
    if (this.sdkPromise) return this.sdkPromise;
    const sdkPromise = this.loadSdk().then(async (sdk) => {
      await sdk.init(this.apiKey, amplitudeOptions).promise;
      await sdk.add(privacyBoundaryPlugin).promise;
      this.sdk = sdk;
      return sdk;
    });
    this.sdkPromise = sdkPromise;
    return sdkPromise.catch((error: unknown) => {
      if (this.sdkPromise === sdkPromise) this.sdkPromise = null;
      throw error;
    });
  }

  private optOutAndReset(sdk: AmplitudeSdk): void {
    sdk.setOptOut(true);
    sdk.reset();
  }
}
